import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api, { mediaUrl } from "@/lib/api";
import { useStoreBase } from "@/lib/store";
import { ArrowLeft } from "lucide-react";
import { formatBlogDate, sanitizeBlogHtml } from "@/lib/storeBlog";

export default function StorefrontBlogPost({ config }) {
  const { slug, base } = useStoreBase();
  const { postSlug } = useParams();
  const [post, setPost] = useState(undefined);

  useEffect(() => {
    if (!config?.store_blog_active || !slug || !postSlug) return;
    api.get(`/store/${slug}/blogs/${postSlug}`).then(({ data }) => setPost(data)).catch(() => setPost(null));
  }, [slug, postSlug, config?.store_blog_active]);

  if (!config?.store_blog_active) return <Navigate to={`${base}/`} replace />;
  if (post === undefined) return <div className="py-24 text-center text-stone-500">Loading…</div>;
  if (!post) return <Navigate to={`${base}/blog`} replace />;

  const storeName = config.store_info?.name || "Store";
  const html = sanitizeBlogHtml(post.body);

  return (
    <article className="bg-[#F6F7F9] min-h-[70vh]" data-testid="store-blog-post">
      <Helmet>
        <title>{post.title} — {storeName}</title>
        <meta name="description" content={post.excerpt || post.title} />
      </Helmet>
      <div className="relative min-h-[320px] sm:min-h-[420px] overflow-hidden bg-stone-900">
        {post.featured_image_url ? (
          <img src={mediaUrl(post.featured_image_url)} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : null}
        <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-900/55 to-stone-900/20" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 pt-24 pb-12 text-white">
          <Link to={`${base}/blog`} className="inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white" data-testid="store-blog-post-back">
            <ArrowLeft className="w-4 h-4" /> All stories
          </Link>
          {post.category_name ? <div className="mt-5 inline-flex rounded-full bg-white/15 px-3 py-1 text-xs font-semibold tracking-wide uppercase">{post.category_name}</div> : null}
          <h1 className="mt-4 font-display text-3xl sm:text-5xl font-extrabold leading-tight drop-shadow">{post.title}</h1>
          <p className="mt-4 text-white/80">{formatBlogDate(post.published_at)}</p>
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 -mt-8 pb-20">
        <div className="rounded-[28px] bg-white border border-stone-100 shadow-[0_20px_60px_rgba(15,23,42,0.08)] p-6 sm:p-10">
          {post.excerpt ? <p className="text-lg text-stone-600 leading-relaxed mb-8">{post.excerpt}</p> : null}
          <div
            className="store-blog-prose text-stone-700 leading-relaxed"
            data-testid="store-blog-body"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </div>
      </div>
      <style>{`
        .store-blog-prose p { margin: 0 0 1.1em; }
        .store-blog-prose h2 { font-family: inherit; font-size: 1.5rem; font-weight: 800; color: #1c1917; margin: 1.6em 0 0.6em; }
        .store-blog-prose h3 { font-size: 1.2rem; font-weight: 700; margin: 1.3em 0 0.5em; }
        .store-blog-prose img { border-radius: 1rem; margin: 1.2em 0; max-width: 100%; height: auto; }
        .store-blog-prose a { color: #0284c7; text-decoration: underline; }
        .store-blog-prose ul, .store-blog-prose ol { margin: 0 0 1.1em 1.2em; }
      `}</style>
    </article>
  );
}
